/**
 * La situation financière de chaque famille, d'un coup d'œil.
 *
 * ## L'avertissement qui est la moitié de cet écran
 *
 * La base annonce **329 207,05 €** de reste dû. Ce chiffre est juste au sens du
 * modèle — il se déduit des allocations de paiement, et d'elles seules — et il
 * est **trompeur** tant que l'historique n'est pas repris : seules 48 factures
 * sur 3 689 portent un paiement rapproché dans le nouveau modèle, alors que
 * l'ancien circuit en connaît 447 de payées.
 *
 * Un écran qui afficherait 329 207,05 € sans le dire ferait croire à autant
 * d'arriérés. Le bandeau du haut le dit, et chaque ligne concernée porte sa
 * propre marque. 399 familles sont dans ce cas.
 *
 * ## Pourquoi rien n'est additionné ici
 *
 * PostgREST rend 1 000 lignes au maximum, et il y a 2 386 familles. Les totaux
 * viennent donc d'une vue d'une ligne, et la liste est paginée. Sommer les 25
 * lignes affichées donnerait un total faux de 99 %.
 */

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Loader2,
  RefreshCw,
  Search,
  Users,
} from 'lucide-react';
import clsx from 'clsx';
import {
  detteExploitable,
  ecartAncienCircuit,
  euros,
  libelle,
  nomFamille,
  retard,
  SITUATIONS,
  type FamilySituation,
} from '../../lib/finances/situation';
import {
  loadFamilies,
  loadFamilyCreditNotes,
  loadFamilyInvoices,
  loadTotals,
  type CreditNoteLine,
  type FinancialTotals,
  type InvoiceLine,
  type TriFamilles,
} from '../../lib/finances/gateway.supabase';
import { LIBELLES_DE_DESTINEE, type Destinee } from '../../lib/creditNotes/destinee';
import { messageErreur } from '../../lib/payments/erreur';
import LienFacture from './components/LienFacture';
import { CHEMINS, ficheUtilisateur } from '../../lib/navigation/chemins';
import { BandeauAncienCircuit } from './components/EtatDesPaiements';

const PAR_PAGE = 25;

const TONS: Record<string, string> = {
  alerte: 'bg-red-100 text-red-800',
  attention: 'bg-amber-100 text-amber-800',
  positif: 'bg-green-100 text-green-800',
  neutre: 'bg-gray-100 text-gray-700',
};

const AdminUsersPage = () => {
  const [totaux, setTotaux] = useState<FinancialTotals | null>(null);
  const [familles, setFamilles] = useState<FamilySituation[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  /*
    La recherche vit dans l'adresse (`?q=`) : la tuile « Données financières »
    de l'accueil Utilisateurs l'y pose, et le retour depuis une fiche la
    retrouve.
  */
  const [parametres, setParametres] = useSearchParams();
  const q = parametres.get('q') ?? '';
  const [saisie, setSaisie] = useState(q);
  const [recherche, setRecherche] = useState(q);
  const [situation, setSituation] = useState('');
  const [ecartSeulement, setEcartSeulement] = useState(false);
  const [tri, setTri] = useState<TriFamilles>('reste');

  const [ouverte, setOuverte] = useState<string | null>(null);
  const [factures, setFactures] = useState<Record<string, InvoiceLine[]>>({});
  const [facturesEnCours, setFacturesEnCours] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, CreditNoteLine[]>>({});

  /*
    La recherche attend que la frappe s'arrête. Sans ce délai, taper « Pinczuk »
    lance sept requêtes dont six sont jetées — et la dernière arrivée n'est pas
    forcément la dernière lancée.
  */
  useEffect(() => {
    const t = setTimeout(() => {
      if (saisie.trim() === recherche.trim()) return;
      setRecherche(saisie);
      setPage(1);
      setParametres(saisie.trim() === '' ? {} : { q: saisie.trim() }, { replace: true });
    }, 350);
    return () => clearTimeout(t);
  }, [saisie, recherche, setParametres]);

  // L'adresse change d'ailleurs (retour arrière, lien) : la recherche la suit.
  useEffect(() => {
    setRecherche((r) => (r.trim() === q.trim() ? r : q));
    setSaisie((s) => (s.trim() === q.trim() ? s : q));
  }, [q]);

  const chargerTotaux = useCallback(async () => {
    try {
      setTotaux(await loadTotals());
    } catch (e) {
      setErreur(messageErreur(e, 'Totaux illisibles'));
    }
  }, []);

  /*
    Un compteur de requêtes : la réponse d'une recherche abandonnée peut
    arriver après celle de la recherche courante, et écraserait l'affichage
    avec un résultat périmé. Seule la dernière lancée a le droit d'écrire.
  */
  const derniere = useRef(0);

  const charger = useCallback(async () => {
    const mienne = ++derniere.current;
    setChargement(true);
    setErreur(null);
    try {
      const r = await loadFamilies({ recherche, situation, ecartSeulement, tri, page, parPage: PAR_PAGE });
      if (mienne !== derniere.current) return;
      setFamilles(r.familles);
      setTotal(r.total);
      setPages(r.pages);
    } catch (e) {
      if (mienne === derniere.current) setErreur(messageErreur(e, 'Liste illisible'));
    } finally {
      if (mienne === derniere.current) setChargement(false);
    }
  }, [recherche, situation, ecartSeulement, tri, page]);

  useEffect(() => { void chargerTotaux(); }, [chargerTotaux]);
  useEffect(() => { void charger(); }, [charger]);

  const deplier = async (f: FamilySituation) => {
    if (ouverte === f.userId) {
      setOuverte(null);
      return;
    }
    setOuverte(f.userId);
    if (factures[f.userId]) return;
    setFacturesEnCours(f.userId);
    try {
      /*
        Les deux ensemble. Demandé le 20 septembre 2026 : « il faudrait pouvoir
        voir les notes de crédit rattachées pour les retrouver facilement ».
        Les charger séparément ferait deux attentes là où une suffit.
      */
      const [lignes, avoirs] = await Promise.all([
        loadFamilyInvoices(f.userId),
        loadFamilyCreditNotes(f.userId),
      ]);
      setFactures((etat) => ({ ...etat, [f.userId]: lignes }));
      setNotes((etat) => ({ ...etat, [f.userId]: avoirs }));
    } catch (e) {
      setErreur(messageErreur(e, 'Factures illisibles'));
    } finally {
      setFacturesEnCours(null);
    }
  };


  return (
    <div className="space-y-6">
      <Link to={CHEMINS.utilisateurs.accueil} className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
        <ArrowLeft className="h-4 w-4" /> Utilisateurs
      </Link>

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Données financières — comptes des familles</h1>
          <p className="text-gray-600">
            Ce que chaque famille a été facturée, a payé, et doit encore. Les totaux de
            l'association sont au Tableau financier.
          </p>
        </div>
        <button
          onClick={() => { void chargerTotaux(); void charger(); }}
          disabled={chargement}
          className="btn-outline flex items-center gap-2 shrink-0"
        >
          <RefreshCw className={clsx('h-4 w-4', chargement && 'animate-spin')} />
          Actualiser
        </button>
      </div>

      {/*
        L'avertissement reste ICI, alors que les totaux sont partis au Tableau
        financier. Ce n'est pas un total : c'est ce qui explique pourquoi le
        reste dû de 399 familles est surévalué, et chaque ligne ci-dessous
        porte sa mention « payé selon l'ancien circuit ». Sans lui, la liste se
        lirait comme 329 207,05 € d'arriérés réels.
      */}
      <BandeauAncienCircuit
        totaux={totaux}
        onFiltrer={() => { setEcartSeulement(true); setPage(1); }}
      />


      {/* --- Les filtres ------------------------------------------------ */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">Chercher</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                value={saisie}
                onChange={(e) => setSaisie(e.target.value)}
                placeholder="Nom, prénom, e-mail, organisation"
                className="form-input w-full pl-9"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">État</label>
            <select
              value={situation}
              onChange={(e) => { setSituation(e.target.value); setPage(1); }}
              className="form-input"
            >
              <option value="">Tous les états</option>
              {SITUATIONS.map((s) => (
                <option key={s} value={s}>{libelle(s).label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Trier par</label>
            <select
              value={tri}
              onChange={(e) => { setTri(e.target.value as TriFamilles); setPage(1); }}
              className="form-input"
            >
              <option value="reste">Reste dû</option>
              <option value="retard">Ancienneté du retard</option>
              <option value="facture">Montant facturé</option>
              <option value="nom">Nom</option>
            </select>
          </div>

          <label className="flex items-center gap-2 text-sm pb-2">
            <input
              type="checkbox"
              checked={ecartSeulement}
              onChange={(e) => { setEcartSeulement(e.target.checked); setPage(1); }}
              className="h-4 w-4 rounded border-gray-300 text-primary-600"
            />
            Écart avec l'ancien circuit
          </label>
        </div>

        <p className="text-xs text-gray-500 mt-3">
          {chargement
            ? 'Chargement…'
            : `${total.toLocaleString('fr-BE')} famille${total > 1 ? 's' : ''} — page ${page} sur ${pages}`}
        </p>
      </div>

      {erreur && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-800 flex gap-2">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          {erreur}
        </div>
      )}

      {/* --- La liste --------------------------------------------------- */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {chargement && familles.length === 0 && (
          <div className="p-10 flex items-center justify-center text-gray-600">
            <Loader2 className="animate-spin h-5 w-5 mr-2" />
            Chargement des familles…
          </div>
        )}

        {!chargement && familles.length === 0 && (
          <div className="p-10 text-center text-gray-500">
            <Users className="h-10 w-10 mx-auto mb-3 text-gray-300" />
            Aucune famille ne correspond à ces critères.
          </div>
        )}

        {familles.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-3">Famille</th>
                  <th className="px-4 py-3">État</th>
                  <th className="px-4 py-3 text-right">Facturé net</th>
                  <th className="px-4 py-3 text-right">Reçu</th>
                  <th className="px-4 py-3 text-right">Reste dû</th>
                  <th className="px-4 py-3">Retard</th>
                  <th className="px-4 py-3 text-right">Provision</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {familles.map((f) => {
                  const l = libelle(f.situation);
                  const ecart = ecartAncienCircuit(f);
                  const depliee = ouverte === f.userId;
                  return (
                    /*
                      La cle est portee par le Fragment, pas par le `<tr>` :
                      c'est le fragment qui est l'element de la liste. Sans
                      cela React avertit, et reconcilie mal les lignes quand
                      le tri change.
                    */
                    <Fragment key={f.userId}>
                      <tr
                        onClick={() => void deplier(f)}
                        className="hover:bg-gray-50 cursor-pointer"
                      >
                        <td className="px-4 py-3">
                          <span className="flex items-start gap-2">
                            {depliee
                              ? <ChevronDown className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />
                              : <ChevronRight className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />}
                            <span>
                              {/*
                                Le nom mène à la fiche. `stopPropagation` n'est pas
                                un détail de style : la ligne porte déjà un `onClick`
                                qui déplie les factures, et sans lui un clic sur le
                                nom ferait LES DEUX — déplier, puis naviguer.
                              */}
                              <Link
                                to={ficheUtilisateur(f.userId)}
                                onClick={(e) => e.stopPropagation()}
                                className="block font-medium text-gray-900 underline decoration-dotted underline-offset-2 hover:decoration-solid hover:text-indigo-700"
                              >
                                {nomFamille(f)}
                              </Link>
                              <span className="block text-xs text-gray-500">{f.email}</span>
                              <span className="block text-xs text-gray-500">
                                {f.invoiceCount} facture{f.invoiceCount > 1 ? 's' : ''} ·{' '}
                                {f.registrationsCount} inscription{f.registrationsCount > 1 ? 's' : ''}
                                {/* Le montant des notes de crédit annonce le tableau
                                    qui les nomme, une fois la ligne dépliée. */}
                                {f.creditNotesCents > 0 && (
                                  <> · <span className="text-indigo-700">
                                    {euros(f.creditNotesCents)} de notes de crédit
                                  </span></>
                                )}
                              </span>
                              {ecart && (
                                <span className="inline-block mt-1 text-xs bg-amber-100 text-amber-900 px-2 py-0.5 rounded">
                                  {euros(ecart.ecartCents)} payés selon l'ancien circuit
                                </span>
                              )}
                            </span>
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            title={l.explication}
                            className={clsx('text-xs px-2 py-0.5 rounded-full whitespace-nowrap', TONS[l.ton])}
                          >
                            {l.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {euros(f.netCents)}
                          {f.creditNotesCents > 0 && (
                            <span className="block text-xs text-gray-500">
                              dont {euros(f.creditNotesCents)} crédités
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">{euros(f.receivedCents)}</td>
                        <td
                          className={clsx(
                            'px-4 py-3 text-right tabular-nums font-medium',
                            detteExploitable(f) && f.daysOverdue > 0 ? 'text-red-700' : 'text-gray-900',
                          )}
                        >
                          {euros(f.outstandingCents)}
                          {!detteExploitable(f) && f.outstandingCents > 0 && (
                            <span className="block text-xs font-normal text-amber-700">à vérifier</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {retard(f.daysOverdue) || '—'}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {f.provisionCents > 0 ? euros(f.provisionCents) : '—'}
                        </td>
                      </tr>

                      {depliee && (
                        <tr className="bg-gray-50">
                          <td colSpan={7} className="px-4 py-4">
                            {facturesEnCours === f.userId && (
                              <span className="flex items-center text-gray-600">
                                <Loader2 className="animate-spin h-4 w-4 mr-2" />
                                Chargement des factures…
                              </span>
                            )}
                            {factures[f.userId]?.length === 0 && (
                              <span className="text-gray-500">Aucune facture.</span>
                            )}
                            {(factures[f.userId] ?? []).length > 0 && (
                              <table className="min-w-full text-xs">
                                <thead>
                                  <tr className="text-left text-gray-500">
                                    <th className="py-1 pr-4">Facture</th>
                                    <th className="py-1 pr-4">Échéance</th>
                                    <th className="py-1 pr-4 text-right">Montant</th>
                                    <th className="py-1 pr-4 text-right">Notes de crédit</th>
                                    <th className="py-1 pr-4 text-right">Reçu</th>
                                    <th className="py-1 pr-4 text-right">Reste</th>
                                    <th className="py-1">État</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(factures[f.userId] ?? []).map((i) => (
                                    <tr key={i.invoiceId} className="border-t border-gray-200">
                                      <td className="py-1.5 pr-4">
                                        <LienFacture invoiceNumber={i.invoiceNumber} pdfUrl={i.pdfUrl} />
                                      </td>
                                      <td className="py-1.5 pr-4 text-gray-600">
                                        {i.dueDate ? new Date(i.dueDate).toLocaleDateString('fr-BE') : '—'}
                                      </td>
                                      <td className="py-1.5 pr-4 text-right tabular-nums">{euros(i.amountCents)}</td>
                                      <td className="py-1.5 pr-4 text-right tabular-nums">
                                        {i.creditNotesCents > 0 ? `- ${euros(i.creditNotesCents)}` : '—'}
                                      </td>
                                      <td className="py-1.5 pr-4 text-right tabular-nums">
                                        {i.allocatedCents > 0 ? euros(i.allocatedCents) : '—'}
                                      </td>
                                      <td className="py-1.5 pr-4 text-right tabular-nums font-medium">
                                        {euros(i.outstandingCents)}
                                      </td>
                                      <td className="py-1.5 text-gray-600">{i.settlement}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}

                            {/*
                              Les notes de crédit, nommées. Demandé le 20 septembre
                              2026 : « il faudrait pouvoir voir les notes de crédit
                              rattachées pour les retrouver facilement ». La colonne
                              « Notes de crédit » du tableau au-dessus n'en donne que
                              le montant, et un montant ne se cherche pas.

                              Elles sont lues par la FAMILLE et non par la facture :
                              une note sans facture — il en existe une — ne figure
                              dans aucune liste indexée sur les factures, et c'est
                              précisément celle qu'on cherche le plus.
                            */}
                            {(notes[f.userId] ?? []).length > 0 && (
                              <div className="mt-4">
                                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                                  Notes de crédit
                                </p>
                                <table className="min-w-full text-xs">
                                  <thead>
                                    <tr className="text-left text-gray-500">
                                      <th className="py-1 pr-4">Note</th>
                                      <th className="py-1 pr-4">Émise le</th>
                                      <th className="py-1 pr-4">Facture</th>
                                      <th className="py-1 pr-4 text-right">Montant</th>
                                      <th className="py-1 pr-4 text-right">Déjà rendu</th>
                                      <th className="py-1 pr-4 text-right">À rendre</th>
                                      <th className="py-1">Décision</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(notes[f.userId] ?? []).map((nc) => {
                                      /*
                                        Une note DÉJÀ remboursée derrière laquelle
                                        aucun euro n'a été encaissé : l'argent est
                                        sorti sans que la famille l'ait versé.
                                        46 notes sont dans ce cas, pour 2 650,20 €,
                                        chez 29 familles.

                                        Le signal ne porte que sur les notes
                                        remboursées : une note simplement déduite
                                        n'a rien derrière elle par construction, et
                                        le signaler sur 926 lignes ne dirait rien.
                                      */
                                      const creux = nc.refundedCents > 0 && nc.cashAdosseCents === 0;
                                      return (
                                        <tr key={nc.creditNoteId} className="border-t border-gray-200">
                                          <td className="py-1.5 pr-4">
                                            <LienFacture
                                              invoiceNumber={nc.creditNoteNumber}
                                              pdfUrl={nc.pdfUrl}
                                            />
                                          </td>
                                          <td className="py-1.5 pr-4 text-gray-600">
                                            {nc.createdAt
                                              ? new Date(nc.createdAt).toLocaleDateString('fr-BE')
                                              : '—'}
                                          </td>
                                          <td className="py-1.5 pr-4">
                                            {nc.invoiceNumber
                                              ? <LienFacture invoiceNumber={nc.invoiceNumber} />
                                              : <span className="text-amber-800">aucune facture</span>}
                                          </td>
                                          <td className="py-1.5 pr-4 text-right tabular-nums text-indigo-800">
                                            {euros(nc.amountCents)}
                                          </td>
                                          <td className="py-1.5 pr-4 text-right tabular-nums text-gray-600">
                                            {nc.refundedCents > 0 ? euros(nc.refundedCents) : '—'}
                                          </td>
                                          <td className={clsx(
                                            'py-1.5 pr-4 text-right tabular-nums',
                                            nc.aRendreCents > 0 ? 'font-medium text-amber-800' : 'text-gray-500',
                                          )}>
                                            {nc.aRendreCents > 0 ? euros(nc.aRendreCents) : '—'}
                                          </td>
                                          <td className="py-1.5 text-gray-600">
                                            {nc.destinee
                                              ? (LIBELLES_DE_DESTINEE[nc.destinee as Destinee]?.label ?? nc.destinee)
                                              : '—'}
                                            {creux && (
                                              <span
                                                title="Cette note a été remboursée, mais sa facture n'a jamais rien encaissé : l'argent est sorti sans que la famille l'ait versé."
                                                className="ml-1 rounded bg-amber-100 px-1 py-0.5 text-[10px] font-medium text-amber-900"
                                              >
                                                remboursée sans encaissement
                                              </span>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || chargement}
              className="btn-outline flex items-center gap-2 disabled:opacity-50"
            >
              <ArrowLeft className="h-4 w-4" /> Précédent
            </button>
            <span className="text-sm text-gray-600">
              Page {page} sur {pages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page === pages || chargement}
              className="btn-outline flex items-center gap-2 disabled:opacity-50"
            >
              Suivant <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsersPage;
